import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ChatWindow.css';
import ChatService from './ChatService';
import { getAuth } from 'firebase/auth';

function ChatWindow({ 
  chatId, 
  projectId,
  briefId,
  campaignId,
  creatorId, 
  creatorName,
  creatorHandle,
  creatorAvatar,
  briefName,
  campaignName,
  chatStatus = 'active',
  onClose 
}) {
  const effectiveProjectId = (projectId || chatId || '').trim();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [chatData, setChatData] = useState(null);
  const [rateLimitWarning, setRateLimitWarning] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messageCountRef = useRef(0);
  const rateLimitResetRef = useRef(null);

  const auth = getAuth();
  const currentUser = auth.currentUser;
  const userType = 'brand'; // This component is for brand dashboard

  const isClosed = (chatData?.status || chatStatus) === 'closed';

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Subscribe to messages
  useEffect(() => {
    if (!effectiveProjectId) return;

    const unsubscribe = ChatService.subscribeToMessages(effectiveProjectId, (msgs) => {
      setMessages(msgs);
    });

    return () => unsubscribe();
  }, [effectiveProjectId]);

  // Subscribe to typing indicator
  useEffect(() => {
    if (!effectiveProjectId) return;

    const unsubscribe = ChatService.subscribeToTyping(effectiveProjectId, (typingData) => {
      // Show typing if the OTHER user is typing (creator in this case)
      setOtherUserTyping(typingData?.creatorTyping || false);
    });

    return () => unsubscribe();
  }, [effectiveProjectId]);

  // Subscribe to chat metadata
  useEffect(() => {
    if (!effectiveProjectId) return;

    const unsubscribe = ChatService.subscribeToChat(effectiveProjectId, (chat) => {
      setChatData(chat);
    });

    return () => unsubscribe();
  }, [effectiveProjectId]);

  // Mark messages as read when chat opens or new messages arrive
  useEffect(() => {
    if (!effectiveProjectId || !currentUser) return;

    const markAsRead = async () => {
      try {
        await ChatService.markMessagesAsRead(effectiveProjectId, currentUser.uid, userType);
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    };

    markAsRead();
  }, [effectiveProjectId, messages, currentUser, userType]);

  // Rate limiting check
  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    
    // Reset counter every minute
    if (!rateLimitResetRef.current || now - rateLimitResetRef.current > 60000) {
      messageCountRef.current = 0;
      rateLimitResetRef.current = now;
      setRateLimitWarning(false);
    }

    // Check if exceeded limit (10 messages per minute)
    if (messageCountRef.current >= 10) {
      setRateLimitWarning(true);
      return false;
    }

    messageCountRef.current++;
    return true;
  }, []);

  // Handle typing indicator with debounce
  const handleTyping = useCallback(() => {
    if (!effectiveProjectId || isClosed) return;

    // Set typing to true
    ChatService.setTypingIndicator(effectiveProjectId, userType, true);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to clear typing after 2 seconds
    typingTimeoutRef.current = setTimeout(() => {
      ChatService.setTypingIndicator(effectiveProjectId, userType, false);
    }, 2000);
  }, [effectiveProjectId, userType, isClosed]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // Enforce character limit
    if (value.length <= 2000) {
      setNewMessage(value);
      handleTyping();
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || isLoading || isClosed) return;

    // Check rate limit
    if (!checkRateLimit()) {
      return;
    }

    setIsLoading(true);

    try {
      await ChatService.sendMessage({
        projectId: effectiveProjectId,
        senderId: currentUser.uid,
        senderType: userType,
        senderName: currentUser.displayName || currentUser.brandName || currentUser.email || 'Brand',
        content: newMessage.trim()
      });

      setNewMessage('');
      setRateLimitWarning(false);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);

      if (diffInHours < 24) {
        // Today - show time only
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffInHours < 48) {
        // Yesterday
        return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        // Older - show date and time
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + 
               ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
    } catch (error) {
      return '';
    }
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;

    messages.forEach((message) => {
      const messageDate = message.timestamp?.toDate ? 
        message.timestamp.toDate().toDateString() : 
        new Date(message.timestamp).toDateString();

      if (messageDate !== currentDate) {
        currentDate = messageDate;
        groups.push({
          type: 'date',
          date: messageDate
        });
      }

      groups.push({
        type: 'message',
        data: message
      });
    });

    return groups;
  };

  const formatDateDivider = (dateString) => {
    const date = new Date(dateString);
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    if (dateString === today) return 'Today';
    if (dateString === yesterday) return 'Yesterday';
    return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const characterCount = newMessage.length;
  const characterLimit = 2000;
  const isNearLimit = characterCount > 1800;

  return (
    <div className="chat-window-ugc3">
      {/* Chat Header */}
      <div className="chat-header-ugc3">
        <div className="chat-header-content-ugc3">
          {creatorAvatar && (
            <img 
              src={creatorAvatar} 
              alt={creatorName}
              className="chat-creator-avatar-ugc3"
              onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }}
            />
          )}
          <div className="chat-creator-info-ugc3">
            <h3 className="chat-creator-name-ugc3">{creatorName}</h3>
            {creatorHandle && (
              <p className="chat-creator-handle-ugc3">{creatorHandle}</p>
            )}
            <p className="chat-brief-name-ugc3">
              {briefName}
              {campaignName && ` • ${campaignName}`}
            </p>
          </div>
        </div>
        <div className="chat-header-status-ugc3">
          {isClosed && (
            <span className="chat-closed-badge-ugc3">Chat Closed</span>
          )}
        </div>
      </div>

      {isClosed && (
        <div className="chat-window-closed-banner">
          This chat is closed because the project is complete.
        </div>
      )}

      {/* Messages Area */}
      <div className="chat-messages-area-ugc3">
        {messages.length === 0 ? (
          <div className="chat-empty-state-ugc3">
            <div className="chat-empty-icon-ugc3">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <h4>Start the conversation</h4>
            <p>Send a message to {creatorName} about {briefName}</p>
          </div>
        ) : (
          <div className="chat-messages-list-ugc3">
            {groupMessagesByDate(messages).map((item, index) => {
              if (item.type === 'date') {
                return (
                  <div key={`date-${index}`} className="chat-date-divider-ugc3">
                    <span>{formatDateDivider(item.date)}</span>
                  </div>
                );
              }

              const message = item.data;
              const isBrand = message.senderType === 'brand';
              const isRead = message.readBy?.length > 1; // More than just sender

              return (
                <div 
                  key={message.id} 
                  className={`chat-message-ugc3 ${isBrand ? 'brand' : 'creator'}`}
                >
                  {!isBrand && (
                    <img 
                      src={creatorAvatar || '/creatorimages/creatorpic1v2-min.png'} 
                      alt={creatorName}
                      className="message-avatar-ugc3"
                      onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }}
                    />
                  )}
                  <div className="message-bubble-ugc3">
                    <div className="message-content-ugc3">{message.content}</div>
                    <div className="message-meta-ugc3">
                      <span className="message-time-ugc3">{formatTime(message.timestamp)}</span>
                      {isBrand && (
                        <span className="message-read-status-ugc3">
                          {isRead ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12l6 6L23 2" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M5 12l5 5L22 5" />
                            </svg>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Typing Indicator */}
            {otherUserTyping && (
              <div className="chat-message-ugc3 creator">
                <img 
                  src={creatorAvatar || '/creatorimages/creatorpic1v2-min.png'} 
                  alt={creatorName}
                  className="message-avatar-ugc3"
                  onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }}
                />
                <div className="message-bubble-ugc3 typing-bubble-ugc3">
                  <div className="typing-indicator-ugc3">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="chat-input-area-ugc3">
        {rateLimitWarning && (
          <div className="rate-limit-warning-ugc3">
            Slow down! You can send up to 10 messages per minute.
          </div>
        )}
        
        {isClosed && (
          <div className="chat-closed-notice-ugc3">
            This chat is closed. You can view the message history but cannot send new messages.
          </div>
        )}

        <form onSubmit={handleSendMessage} className="chat-input-form-ugc3">
          <div className="chat-input-wrapper-ugc3">
            <textarea
              value={newMessage}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              placeholder={isClosed ? 'Chat is closed' : 'Type a message...'}
              className="chat-input-ugc3"
              disabled={isClosed || isLoading}
              rows="1"
            />
            {isNearLimit && (
              <span className={`char-count-ugc3 ${characterCount >= characterLimit ? 'limit-reached' : ''}`}>
                {characterCount}/{characterLimit}
              </span>
            )}
          </div>
          <button 
            type="submit" 
            className="chat-send-btn-ugc3"
            disabled={isClosed || isLoading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChatWindow;

